import * as THREE from 'three'
import ZapparWebGLSnapshot from '@zappar/webgl-snapshot'

import Experience from '.'

import LoadingManager from './Utils/LoadingManager'

import logoSrc from '../assets/photo/logo.png'
import nestleSrc from '../assets/photo/nestle.png'
import tickSrc from '../assets/photo/tick.png'
import copySrc from '../assets/photo/copyright_22.png'

import Analytics from '../config/analytics'

/**
 * WebGL Canvas snapshot
 */
export default class Snapshot {
  private experience = new Experience()

  private group = new THREE.Group()

  private takeScreenshot = false
  private photo!: string

  constructor(private loader = new THREE.TextureLoader(LoadingManager)) {
    this.init()
    this.hide()
  }

  private show() {
    this.group.visible = true
  }

  private hide() {
    this.group.visible = false
  }

  private applyTextureEncoding(texture: THREE.Texture) {
    texture.encoding = THREE.sRGBEncoding
    texture.format = THREE.RGBAFormat
  }

  private init() {
    const { sceneOrtho } = this.experience
    sceneOrtho.add(this.group)

    const wWidth = window.innerWidth / 2
    const wHeight = window.innerHeight / 2

    const spriteMaterial = new THREE.SpriteMaterial()

    // Logo ~ Bottom Left ~
    this.loader.load(logoSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const logoSpriteMaterial = spriteMaterial.clone()
      logoSpriteMaterial.map = texture

      const { width, height } = logoSpriteMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const logoSprite = new THREE.Sprite(logoSpriteMaterial)
      logoSprite.center.set(0.0, 0.0)
      logoSprite.scale.set(width, height, 1)

      logoSprite?.position.set(-wWidth, -wHeight, 1)

      this.group.add(logoSprite)
    })

    // Nestle ~ Top Left ~
    this.loader.load(nestleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const nestleSpriteMaterial = spriteMaterial.clone()
      nestleSpriteMaterial.map = texture

      const { width, height } = nestleSpriteMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const nestleSprite = new THREE.Sprite(nestleSpriteMaterial)
      nestleSprite.center.set(0.0, 1.0)
      nestleSprite.scale.set(width, height, 1)

      nestleSprite?.position.set(-wWidth + 15, wHeight - 15, 1) // top left

      this.group.add(nestleSprite)
    })

    // Tick ~ Top Right ~
    this.loader.load(tickSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const tickSpriteMaterial = spriteMaterial.clone()
      tickSpriteMaterial.map = texture

      const { width, height } = tickSpriteMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const tickSprite = new THREE.Sprite(tickSpriteMaterial)
      tickSprite.center.set(1.0, 1.0)
      tickSprite.scale.set(width, height, 1)

      tickSprite.position.set(wWidth - 10, wHeight - 7, 1)

      this.group.add(tickSprite)
    })

    // Copy ~ Bottom Right ~
    this.loader.load(copySrc, (texture) => {
      this.applyTextureEncoding(texture)

      const copySpriteMaterial = spriteMaterial.clone()
      copySpriteMaterial.map = texture

      const { width, height } = copySpriteMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const copySprite = new THREE.Sprite(copySpriteMaterial)
      copySprite.center.set(1.0, 0.0)
      copySprite.scale.set(width, height, 1)

      copySprite?.position.set(wWidth - 8, -wHeight + 6, 1) // bottom right

      this.group.add(copySprite)
    })
  }

  public capture() {
    this.show()
    this.takeScreenshot = true

    setTimeout(() => {
      try {
        ZapparWebGLSnapshot({
          data: this.photo,
          fileNamePrepend: 'Crayola',
          onClose: () => this.hide(),
          onShare: () => Analytics.event.share_button_click(),
          onSave: () => Analytics.event.save_photo_click(),
        })
      } catch (err) {
        console.log('ZapparWebGLSnapshot - error ', { err })
      }
    }, 50)
  }

  public update() {
    if (this.takeScreenshot) {
      this.takeScreenshot = false

      this.photo = this.experience.renderer.instance.domElement.toDataURL(
        'image/jpeg',
        0.8
      )
    }
  }
}
